import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  DocumentTaskCreatedPayload,
  MessagePayload,
  MessageResponse,
  QuestionTaskCreatedPayload,
  SectionTaskCreatedPayload,
  TAssignTaskRequest,
  TBulkUpdateTaskStatusRequest,
  TCreateQuestionTaskRequest,
  TCreateSectionTaskRequest,
  TSearchTaskInstanceRequest,
  TTaskInstanceDetailResponse,
  TTaskInstanceDropdownResponse,
  TTaskListDropdownQueryParams,
  TUpdateTaskStatusRequest,
  TaskInstance,
  TaskInstanceDetail,
  ThreadMessage,
} from '../types';

import { WORKSPACE_URLS } from '@data-access/shared';

@Injectable({ providedIn: 'root' })
export class TasksService {
  private readonly _http = inject(HttpClient);

  /*
   * Fetches a list of task instances for a given workspace.
   * @param workspaceId - The ID of the workspace to fetch tasks from.
   * @param params - Optional parameters to filter the task list.
   */
  getTaskList(workspaceId: string, data: TSearchTaskInstanceRequest) {
    return this._http.post<TaskInstance[]>(
      `${WORKSPACE_URLS?.V2}${workspaceId}/tasks/`,
      data,
    );
  }

  /*
   * Fetches a list of task instances for a given taskIds
   * */
  getTaskListByIds(workspaceId: string, data: TSearchTaskInstanceRequest) {
    return this._http.post<TaskInstance[]>(
      `${WORKSPACE_URLS?.V2}${workspaceId}/tasks/`,
      data,
    );
  }

  /**
   * Fetches a list of task instances for a given workspace as dropdown items.
   * @param workspaceId - The ID of the workspace to fetch tasks from.
   * @param params - Parameters to filter the task list.
   * @returns An observable that emits the task instance dropdown items.
   */
  getTaskListAsDropdownItems(
    workspaceId: string,
    data: TTaskListDropdownQueryParams,
  ) {
    return this._http.post<TTaskInstanceDropdownResponse[]>(
      `${WORKSPACE_URLS.V2}${workspaceId}/tasks/`,
      data,
    );
  }

  /*
   * Fetches the details of a specific task instance by its ID.
   * @param {workspaceId} - The ID of the workspace containing the task.
   * @param {taskInstanceId} - The ID of the task instance to retrieve.
   * @return An observable that emits the task instance details.
   */
  getTaskById(workspaceId: string, taskInstanceId: string) {
    return this._http.get<TaskInstanceDetail>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/${taskInstanceId}/`,
    );
  }

  /*
   ** Assigns a task to a specific user.
   * @param {string} workspaceId - The ID of the workspace containing the task.
   * @param {string} taskInstanceId - The ID of the task instance to be assigned.
   * @param {string} assigneeId - The ID of the user to whom the task will be assigned.
   * @return An observable that emits the server response.
   **/
  assignTask(
    workspaceId: string,
    taskInstanceId: string,
    request: TAssignTaskRequest,
  ) {
    return this._http.post<TTaskInstanceDetailResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/${taskInstanceId}/assign/`,
      request,
    );
  }

  /*
   * Updates the status of a specific task instance.
   * @param {string}
   * workspaceId - The ID of the workspace containing the task.
   *   @param {string} taskInstanceId - The ID of the task instance to be updated.
   *   @param {TaskStatus} status - The new status to set for the task instance.
   *   @return An observable that emits the updated task instance.
   */
  updateTaskStatus(
    workspaceId: string,
    taskInstanceId: string,
    request: TUpdateTaskStatusRequest,
  ) {
    return this._http.post<TTaskInstanceDetailResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/${taskInstanceId}/status/`,
      request,
    );
  }

  bulkUpdateTaskStatus(
    workspaceId: string,
    request: TBulkUpdateTaskStatusRequest,
  ) {
    return this._http.put<string[]>(
      `${WORKSPACE_URLS.V2}${workspaceId}/tasks/`,
      request,
    );
  }

  /**
   * Creates a task for a section in a workspace.
   * @param workspaceId - The ID of the workspace to create the task for.
   * @param request - The request body containing the section ID and task data.
   * @returns An observable that emits the created task instance.
   */
  createTaskForSection(
    workspaceId: string,
    request: TCreateSectionTaskRequest,
  ) {
    return this._http.post<TTaskInstanceDetailResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/section/`,
      request,
    );
  }

  /**
   * Creates a task for a question in a workspace.
   * @param workspaceId - The ID of the workspace to create the task for.
   * @param request - The request body containing the question ID and task data.
   * @returns An observable that emits the created task instance.
   */
  createTaskForQuestion(
    workspaceId: string,
    request: TCreateQuestionTaskRequest,
  ) {
    return this._http.post<TTaskInstanceDetailResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/question/`,
      request,
    );
  }

  /**
   * Retrieves messages for a specific thread in a workspace.
   * @param workspaceId - The ID of the workspace containing the thread.
   * @param threadId - The ID of the thread to retrieve messages from.
   * @returns An observable that emits the thread messages.
   */
  getThreadMessages(workspaceId: string, threadId: string) {
    return this._http.get<ThreadMessage[]>(
      `${WORKSPACE_URLS.V2}${workspaceId}/threads/${threadId}/`,
    );
  }

  /**
   * Creates a new message in a specific thread within a workspace.
   * @param workspaceId - The ID of the workspace containing the thread.
   * @param taskInstanceId - The ID of the task instance associated with the thread.
   * @param message - The message content and metadata to be created.
   * @returns An observable that emits the created thread message.
   */
  createThreadMessage(
    workspaceId: string,
    taskInstanceId: string,
    message: MessagePayload,
  ) {
    return this._http.post<MessageResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/tasks/${taskInstanceId}/message/`,
      message,
    );
  }
  /**
   * Retrieves the thread ID associated with a specific task instance in a workspace.
   * @param workspaceId - The ID of the workspace containing the task.
   * @param taskInstanceId - The ID of the task instance to retrieve the thread for.
   * @returns An observable that emits the thread ID.
   */
  getThreadForTaskInstance(workspaceId: string, taskInstanceId: string) {
    return this._http.get<{ thread_id: string }>(
      `${WORKSPACE_URLS.V2}${workspaceId}/tasks/${taskInstanceId}/thread/`,
    );
  }

  /**
   * Creates a document-related task in a specified workspace.
   * @param workspaceId - The ID of the workspace where the task will be created.
   * @param request - The request body containing task details such as title, description, assignee, and document ID.
   * @returns An observable that emits the created task instance details.
   */
  createDocumentTask(workspaceId: string, request: DocumentTaskCreatedPayload) {
    return this._http.post<TTaskInstanceDetailResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/document/`,
      this._encodeDataPayload(request),
    );
  }

  /**
   * Creates a question-related task in a specified workspace.
   * @param workspaceId - The ID of the workspace where the task will be created.
   * @param request - The request body containing task details such as title, description, assignee, and question ID.
   * @returns An observable that emits the created task instance details.
   */
  createQuestionTask(workspaceId: string, request: QuestionTaskCreatedPayload) {
    return this._http.post<TTaskInstanceDetailResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/question/`,
      this._encodeDataPayload(request),
    );
  }

  /**
   * Creates a section-related task in a specified workspace.
   * @param workspaceId - The ID of the workspace where the task will be created.
   * @param request - The request body containing task details such as title, description, assignee, and question ID.
   * @returns An observable that emits the created task instance details.
   */
  createSectionTask(workspaceId: string, request: SectionTaskCreatedPayload) {
    return this._http.post<TTaskInstanceDetailResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/question/`,
      request,
    );
  }

  /**
   * Exports the messages of a specific thread in a workspace.
   * @param workspaceId - The ID of the workspace containing the thread.
   * @param threadId - The ID of the thread to export messages from.
   * @returns An observable that emits the exported messages as a Blob.
   */
  exportThreadMessages(workspaceId: string, threadId: string) {
    return this._http.get<Blob>(
      `${WORKSPACE_URLS.V2}${workspaceId}/threads/${threadId}/export/`,
      {
        observe: 'response',
        responseType: 'blob' as 'json',
      },
    );
  }

  /**
   * Exports all threads in a workspace.
   * @param workspaceId - The ID of the workspace to export threads from.
   * @returns An observable that emits the exported threads as a Blob.
   */
  exportAllThreads(workspaceId: string) {
    return this._http.get<Blob>(
      `${WORKSPACE_URLS.V2}${workspaceId}/threads/export/`,
      {
        observe: 'response',
        responseType: 'blob' as 'json',
      },
    );
  }

  /**
   * Sends a manual notification email to all workspace recipients.
   * @param workspaceId - The ID of the workspace to send notifications for.
   * @returns An observable that emits the server response.
   */
  sendManualNotification(workspaceId: string) {
    return this._http.post<{ message: string }>(
      `${WORKSPACE_URLS.V2}${workspaceId}/notify/`,
      {},
    );
  }

  private _encodeDataPayload(
    payload:
      | SectionTaskCreatedPayload
      | QuestionTaskCreatedPayload
      | DocumentTaskCreatedPayload,
  ) {
    try {
      // Convert the string to UTF-8 bytes first to handle Unicode characters
      const jsonString = JSON.stringify(payload);
      const utf8Bytes = new TextEncoder().encode(jsonString);
      // Convert bytes to binary string that btoa can handle
      const binaryString = Array.from(utf8Bytes)
        .map((byte) => String.fromCharCode(byte))
        .join('');
      // Encode to base64
      return {
        data: btoa(binaryString),
      };
    } catch (e) {
      // Fallback to original method if TextEncoder is not available
      console.error(`Error occurred during encoding payload: ${e}`);
      return {
        data: btoa(JSON.stringify(payload)),
      };
    }
  }
}
